import React from "react";
// react library for routing
import { useLocation, NavLink as NavLinkRRD } from "react-router-dom";
// nodejs library that concatenates classes
import classnames from "classnames";
// nodejs library to set properties for components
import { PropTypes } from "prop-types";
// react library that creates nice scrollbar on windows devices
import PerfectScrollbar from "react-perfect-scrollbar";
// reactstrap components
import {
  Collapse,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Badge,
} from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import userServices from "services/userServices";

function Sidebar({ toggleSidenav, sidenavOpen, routes, logo, rtlActive }) {
  const { t } = useTranslation();
  const [state, setState] = React.useState({});
  const [notifications, setNotifications] = React.useState([]);
  const location = useLocation();

  React.useEffect(() => {
    setState(getCollapseStates(routes));
    // eslint-disable-next-line
  }, []);

  const fetchUserNotifications = React.useCallback(async () => {
    try {
      const res = await userServices.fetchNotifications();
      const notifs = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.notifications)
        ? res.notifications
        : [];
      setNotifications(notifs.filter((n) => n && n.read !== true));
    } catch (err) {
      console.error("Error fetching notifications for sidebar:", err);
    }
  }, []);

  React.useEffect(() => {
    fetchUserNotifications();
    const intervalNotif = setInterval(fetchUserNotifications, 15000);
    window.addEventListener("notificationsRead", fetchUserNotifications);

    return () => {
      clearInterval(intervalNotif);
      window.removeEventListener("notificationsRead", fetchUserNotifications);
    };
  }, [fetchUserNotifications]);

  const getNotificationCountForRoute = React.useCallback((prop, notifs) => {
    if (!notifs || !notifs.length) return 0;

    const isMatch = (notif, route) => {
      if (!notif || notif.read === true) return false;

      const targetLink = String(notif.targetLink || "").toLowerCase();
      const notifType = String(notif.type || "").toLowerCase();
      const safeSubject = String(notif.subject || notif.subtitle || "").toLowerCase();
      const path = String(route?.path || "").toLowerCase();
      const key = String(route?.permissionKey || "").toLowerCase();

      // 1. Direct targetLink match
      if (targetLink && path && (targetLink.includes(path) || path.includes(targetLink))) {
        return true;
      }

      // 2. Type / PermissionKey mapping
      if (path.includes("/users") || key === "users") {
        return (
          ["user", "users", "user_registration", "user_signup", "kyc"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("user") || safeSubject.includes("signup") || safeSubject.includes("profile")))
        );
      }

      if (path.includes("/all-listings") || key === "listings") {
        return (
          ["listing", "listings", "sender_listing", "traveler_listing", "offer"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("listing") || safeSubject.includes("package") || safeSubject.includes("baggage")))
        );
      }

      if (path.includes("/all-orders") || key === "orders") {
        return (
          ["order", "orders", "delivery", "deliveries", "payment", "transaction"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("order") || safeSubject.includes("delivery") || safeSubject.includes("payment")))
        );
      }

      if (path.includes("/support-tickets") || key === "supporttickets") {
        return (
          ["support", "ticket", "support_ticket", "supporttickets", "dispute", "support-request"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("ticket") || safeSubject.includes("support") || safeSubject.includes("dispute")))
        );
      }

      if (path.includes("/broadcasts") || key === "broadcasts") {
        return (
          ["broadcast", "broadcasts", "announcement"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("broadcast") || safeSubject.includes("announcement")))
        );
      }

      if (path.includes("/promo-codes") || key === "promocodes") {
        return (
          ["promo", "promocode", "promo_code", "promocodes", "coupon"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("promo") || safeSubject.includes("coupon")))
        );
      }

      if (path.includes("/messages") || key === "messages") {
        return (
          ["message", "messages", "chat", "conversation"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("message") || safeSubject.includes("chat")))
        );
      }

      if (path.includes("/role") || key === "rolepage") {
        return (
          ["role", "roles", "permission", "permissions"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("role") || safeSubject.includes("permission")))
        );
      }

      if (path === "/admin/moderation" || (path.includes("/moderation") && !path.includes("/verification") && !path.includes("/behavioral") && !path.includes("/appeals"))) {
        return (
          ["moderation", "flag", "flagged"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("moderation") || safeSubject.includes("flag")))
        );
      }

      if (path.includes("/moderation/verification")) {
        return ["verification", "verification_queue", "identity_verification"].includes(notifType);
      }

      if (path.includes("/moderation/behavioral")) {
        return ["behavioral", "behavior", "behavioral_queue"].includes(notifType);
      }

      if (path.includes("/moderation/appeals")) {
        return ["appeal", "appeals", "appeals_queue"].includes(notifType);
      }

      if (path.includes("/admin-action")) {
        return ["admin_action", "audit"].includes(notifType) || (notifType === "system" && safeSubject.includes("action"));
      }

      if (path.includes("/failedlogin")) {
        return (
          ["failed_login", "login_attempt", "security", "ip_block", "blocked_ip"].includes(notifType) ||
          (notifType === "system" && (safeSubject.includes("login") || safeSubject.includes("ip") || safeSubject.includes("security") || safeSubject.includes("block"))) ||
          targetLink.includes("/failedlogin")
        );
      }

      if (path.includes("/notification-templates") || key === "notificationtemplate") {
        return (
          ["template", "notification_template", "templates"].includes(notifType) ||
          (notifType === "system" && safeSubject.includes("template"))
        );
      }

      return false;
    };

    if (prop.collapse && Array.isArray(prop.views)) {
      return notifs.filter((n) => prop.views.some((v) => isMatch(n, v))).length;
    }

    return notifs.filter((n) => isMatch(n, prop)).length;
  }, []);

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname.indexOf(routeName) > -1 ? "active" : "";
  };
  // makes the sidenav normal on hover (actually when mouse enters on it)
  const onMouseEnterSidenav = () => {
    if (!document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.add("g-sidenav-show");
    }
  };
  // makes the sidenav mini on hover (actually when mouse leaves from it)
  const onMouseLeaveSidenav = () => {
    if (!document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.remove("g-sidenav-show");
    }
  };
  // this creates the intial state of this component based on the collapse routes
  // that it gets through routes
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop, key) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.js - route /admin/regular-forms
  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname.indexOf(routes[i].path) !== -1) {
        return true;
      }
    }
    return false;
  };
  // this is used on mobile devices, when a user navigates
  // the sidebar will autoclose
  const closeSidenav = () => {
    if (window.innerWidth < 1200) {
      toggleSidenav();
    }
  };
  const routeNotificationCounts = React.useMemo(() => {
    const countsMap = new Map();
    const calculateCounts = (routeList) => {
      for (const route of routeList) {
        if (route.redirect) continue;
        const count = getNotificationCountForRoute(route, notifications);
        countsMap.set(route, count);
        if (route.views) {
          calculateCounts(route.views);
        }
      }
    };
    calculateCounts(routes);
    return countsMap;
  }, [routes, notifications, getNotificationCountForRoute]);

  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinks = (routes) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      const count = routeNotificationCounts.get(prop) || 0;

      if (prop.collapse) {
        var st = {};
        st[prop["state"]] = !state[prop.state];
        return (
          <NavItem key={key}>
            <NavLink
              data-toggle="collapse"
              aria-expanded={state[prop.state]}
              className={classnames(
                "sidenav-toggler d-flex align-items-center justify-content-between py-2",
                {
                  active: getCollapseInitialState(prop.views),
                }
              )}
              onClick={(e) => {
                e.preventDefault();
                setState(st);
              }}
            >
              <div className="d-flex align-items-center sidebar-link-content">
                {prop.icon ? (
                  <>
                    <i className={prop.icon} />
                    <span className="nav-link-text ml-2">{t(prop.name)}</span>
                  </>
                ) : prop.miniName ? (
                  <>
                    <span className="sidenav-mini-icon"> {prop.miniName} </span>
                    <span className="sidenav-normal ml-2"> {t(prop.name)} </span>
                  </>
                ) : null}
              </div>
              {count > 0 && (
                <Badge color="danger" pill className="sidebar-notif-badge font-weight-bold mx-2 flex-shrink-0">
                  {count}
                </Badge>
              )}
            </NavLink>
            <Collapse isOpen={state[prop.state]}>
              <Nav className="nav-sm flex-column">
                {createLinks(prop.views)}
              </Nav>
            </Collapse>
          </NavItem>
        );
      }
      return (
        <NavItem className={activeRoute(prop.layout + prop.path)} key={key}>
          <NavLink
            to={prop.layout + prop.path}
            onClick={closeSidenav}
            tag={NavLinkRRD}
            className="d-flex align-items-center justify-content-between py-2"
          >
            <div className="d-flex align-items-center sidebar-link-content">
              {prop.icon !== undefined ? (
                <>
                  <i className={prop.icon} />
                  <span className="nav-link-text ml-2">{t(prop.name)}</span>
                </>
              ) : prop.miniName !== undefined ? (
                <>
                  <span className="sidenav-mini-icon"> {prop.miniName} </span>
                  <span className="sidenav-normal ml-2"> {t(prop.name)} </span>
                </>
              ) : (
                <span className="nav-link-text">{t(prop.name)}</span>
              )}
            </div>
            {count > 0 && (
              <Badge color="danger" pill className="sidebar-notif-badge font-weight-bold ml-2 flex-shrink-0">
                {count}
              </Badge>
            )}
          </NavLink>
        </NavItem>
      );
    });
  };

  const scrollBarInner = (
    <div className="scrollbar-inner">
      <div className="sidenav-header d-flex align-items-center">
        {logo ? (
          <img
            alt={logo.imgAlt}
            className="navbar-brand-img navbar-brand-img-custom clickable-logo"
            src={logo.imgSrc}
            onClick={() =>
              window.open(logo.link || "https://bagmails.com/", "_blank", "noopener,noreferrer")
            }
          />
        ) : null}
        <div className="ml-auto">
          <div
            className={classnames("sidenav-toggler d-none d-xl-block", {
              active: sidenavOpen,
            })}
            onClick={toggleSidenav}
          >
            <div className="sidenav-toggler-inner">
              <i className="sidenav-toggler-line" />
              <i className="sidenav-toggler-line" />
              <i className="sidenav-toggler-line" />
            </div>
          </div>
        </div>
      </div>
      <div className="navbar-inner">
        <Collapse navbar isOpen={true}>
          <Nav navbar>{createLinks(routes)}</Nav>
          <h6 className="navbar-heading p-0 text-muted">
          </h6>
          <Nav className="mb-md-3" navbar>
          </Nav>
        </Collapse>
      </div>
    </div>
  );
  return (
    <Navbar
      className={
        "sidenav navbar-vertical navbar-expand-xs navbar-light bg-white " +
        (rtlActive ? "" : "fixed-left")
      }
      onMouseEnter={onMouseEnterSidenav}
      onMouseLeave={onMouseLeaveSidenav}
    >
      {navigator.platform.indexOf("Win") > -1 ? (
        <PerfectScrollbar>{scrollBarInner}</PerfectScrollbar>
      ) : (
        scrollBarInner
      )}
    </Navbar>
  );
}

Sidebar.defaultProps = {
  routes: [{}],
  toggleSidenav: () => { },
  sidenavOpen: false,
  rtlActive: false,
};

Sidebar.propTypes = {
  toggleSidenav: PropTypes.func,
  sidenavOpen: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired,
  }),
  rtlActive: PropTypes.bool,
};

export default Sidebar;
